import type { ReactNode } from 'react';

/**
 * 화면 상단의 제목 + 설명 + 우측 동작 묶음.
 *
 * v1 은 화면마다 같은 마크업을 복사해 두었고, 그래서 제목 크기와 여백이 화면마다 조금씩 다르다.
 * 한 곳에 모아 두면 이관되는 화면들이 저절로 같은 모양을 갖는다.
 *
 * `data-tour="page-actions"` 는 화면별 안내(?)가 밝힐 자리다({@code lib/tour/pageGuides.ts}). 여기 한 번
 * 달아 두면 이 컴포넌트를 쓰는 모든 화면이 안내 대상을 갖는다 — 화면마다 따로 붙이면 반드시 빠뜨리는
 * 곳이 생긴다. `actions` 가 없는 화면에는 그 요소 자체가 없고, 안내는 해당 걸음을 조용히 건너뛴다.
 */
export function PageHeader({
  title,
  titleAside,
  description,
  actions,
}: {
  title: string;
  /**
   * 제목 <b>바로 옆</b>에 붙는 것 (사용자 지시 2026-09-29 — 「이 버튼은 기안하기 제목 바로 옆으로」).
   *
   * <p>{@link actions} 와 자리가 다르다. 저쪽은 <b>오른쪽 끝</b>에 붙는 「이 화면으로 무엇을 하는가」
   * (글쓰기·상신·인쇄)이고, 이쪽은 <b>제목의 일부처럼</b> 읽히는 것 — 지금 무엇을 보고 있는지를
   * 가르는 고르개다(기안하기의 「기안서 / 자료입력」). 오른쪽 끝에 두면 단추 다섯 사이에 섞여
   * 「누르는 것」으로 보이고, 화면이 통째로 바뀌는 고르개라는 사실이 읽히지 않는다.
   *
   * <p>제목이 길면 이 칸도 함께 줄어든다(`min-w-0`) — 제목만 접히고 이것이 남으면 더 이상하다.
   */
  titleAside?: ReactNode;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    /*
      ★ 버튼은 <b>어느 폭에서나 오른쪽</b>이다 (사용자 지시 2026-08-31).

      예전에는 `sm`(640) 아래에서 세로로 쌓였다. 그러면 모바일에서 「글쓰기」 가 제목 아래 <b>왼쪽</b>에
      혼자 한 줄을 차지해, 목록이 시작하기 전에 화면 한 뼘이 머리말로 사라졌다.

      <p>가로를 유지하되 제목 쪽에 {@code min-w-0} 을 준다 — 그것이 없으면 flex 항목의 최소 폭이
      `auto` 라 긴 제목이 칸을 뚫고 버튼을 화면 밖으로 밀어낸다.
    */
    /*
      ★ 버튼은 <b>어느 폭에서나 오른쪽</b>이다 (사용자 지시 2026-08-31). 예전에는 `sm`(640) 아래에서
      세로로 쌓여, 모바일에서 「글쓰기」 가 제목 아래 <b>왼쪽</b>에 혼자 한 줄을 차지했다.

      <p>다만 <b>제목을 줄여서 자리를 만들지는 않는다</b>. 그렇게 했다가 버튼이 여럿인 화면
      (전자결재)에서 「전자결재」 네 글자가 <b>한 글자씩 네 줄</b>로 접혔다 — 제목이 제목으로
      안 읽힌다. 제목은 접지 않고, 자리가 정 모자라면 <b>버튼 묶음이 통째로 아랫줄</b>로 내려간다
      (바깥 상자의 `flex-wrap`). 내려가도 `ml-auto` 라 오른쪽에 붙는다.

      ★★ 좁은 화면(<768)만 다르게 간다 (사용자 지시 2026-08-31).
      <b>설명은 감추고, 버튼은 제목 바로 옆에 붙인다.</b> 제목 아래 설명 두 줄 + 버튼 한 줄이면
      목록이 시작하기 전에 화면 한 뼘이 머리말이었다. 여기서 설명은 「무슨 화면인지」를 다시 말할
      뿐이고, 그건 방금 누른 메뉴 이름이 이미 말한다.

      <p>버튼이 아랫줄로 밀리던 것도 이 칸 때문이다 — 이 칸의 flex-basis 가 <b>내용 폭</b>이라
      설명 문장 하나로 한 줄이 다 찼다. 모바일에서는 basis 를 0 으로 주고 제목이 대신 줄어들게
      한다(`truncate`). 대신 <b>바닥은 남긴다</b>(min-width 72px) — 버튼이 다섯쯤 되는 화면에서
      제목이 한 글자로 뭉개지느니 예전처럼 버튼 묶음이 아랫줄로 내려가는 편이 낫다.

      <p>`md:` 로 되돌리는 값들은 <b>웹 화면을 한 픽셀도 바꾸지 않기 위한 것</b>이다
      (사용자 지시 2026-08-31 · 웹은 손대지 않는다). basis-auto·flex-initial·min-w-0 은 모두
      이 클래스들을 붙이기 전의 기본값이다.
    */
    <div className="flex flex-row flex-wrap items-start justify-between gap-x-3 gap-y-2 no-print">
      <div className="flex-1 basis-0 min-w-[72px] md:flex-initial md:basis-auto md:min-w-0">
        {/*
          `data-page-title` 은 <b>앱에서만</b> 쓴다 (사용자 지시 2026-09-22: 「부서관리 밑에꺼 지우자」).

          원격 화면은 껍데기(`AppShell`)가 이미 상단바에 제목을 그린 자리에 뜨므로, 이 줄까지
          서면 같은 이름이 위아래로 두 번 난다. 그래서 앱이 이 표시만 잡아 감춘다
          ({@code mobile/src/remote/RemoteScreen.tsx} 의 주입 CSS).

          <p>머리 칸을 통째로 감추지 않는 이유는 오른쪽의 `actions` 다 — 그쪽을 함께 지우면
          화면의 주된 버튼이 같이 사라진다. 웹에서는 아무 일도 일어나지 않는다(표시일 뿐이다).
        */}
        {/*
          제목과 그 옆 칸은 <b>한 줄</b>이다. `titleAside` 가 없으면 예전과 한 픽셀도 다르지 않다 —
          감싸는 상자가 없을 때와 같은 자리에 서도록 `min-w-0` 만 얹는다.
        */}
        <div className="flex min-w-0 items-center gap-3">
          <h1 data-page-title className="text-xl font-bold truncate" style={{ color: 'var(--text-primary)' }}>
            {title}
          </h1>
          {titleAside && <div className="shrink-0">{titleAside}</div>}
        </div>
        {description && (
          <p className="text-sm mt-1 hidden md:block" style={{ color: 'var(--text-muted)' }}>
            {description}
          </p>
        )}
      </div>
      {actions && (
        <div className="flex items-center justify-end gap-2 flex-wrap ml-auto" data-tour="page-actions">
          {actions}
        </div>
      )}
    </div>
  );
}

/** 목록이 비었을 때의 카드. v1 이 화면마다 다른 문구·여백으로 반복하던 것. */
export function EmptyCard({ children }: { children: ReactNode }) {
  return (
    <div className="card p-8 text-center" style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
      {children}
    </div>
  );
}

/**
 * 화면 전체가 비었을 때. 목록 한 칸이 비는 `EmptyCard` 와 달리 "다음에 뭘 해야 하는지"를 알려준다.
 * v1은 화면마다 아이콘 SVG 를 인라인으로 복사해 두어 같은 상황에 다른 그림이 떴다.
 */
export function EmptyState({
  icon = 'calendar',
  title,
  description,
}: {
  icon?: 'calendar' | 'site' | 'check' | 'click';
  title: string;
  description?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'var(--bg-hover)' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--text-muted)' }}>
          {icon === 'calendar' && (
            <>
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </>
          )}
          {icon === 'site' && <polygon points="3 11 22 2 13 21 11 13 3 11" />}
          {icon === 'check' && <path d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />}
          {icon === 'click' && <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />}
        </svg>
      </div>
      <div className="text-center">
        <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</p>
        {description && (
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{description}</p>
        )}
      </div>
    </div>
  );
}

/** 데이터를 기다리는 동안의 자리. */
export function LoadingBlock() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="spinner w-8 h-8" />
    </div>
  );
}
